/**
 * Microsoft Ads (Bing UET) Utility
 * Handles initialization for Microsoft Ads Universal Event Tracking
 * Note: Only runs in production/live environment
 */

import { config } from '../config/env'
import { logger } from '../utils/logger'

/**
 * Initialize Microsoft Ads UET tag
 * Loads the Bing UET script with the tag ID from environment
 * Only initializes if NOT in test mode (i.e., production/live only)
 */
export const initializeMicrosoftAds = () => {
  const tagId = config.microsoftAdsTagId

  if (!tagId) {
    logger.debug('ℹ️ Microsoft Ads not initialized: No tag ID provided')
    return
  }

  if (config.testMode) {
    logger.debug('ℹ️ Microsoft Ads skipped: Test/Staging mode detected')
    return
  }

  if (window.uetq && window.uetq instanceof Array === false) {
    logger.debug('ℹ️ Microsoft Ads UET already initialized')
    return
  }

  logger.debug('📊 Initializing Microsoft Ads UET:', tagId)

  try {
    (function(w, d, t, u, o) {
      w[u] = w[u] || []
      o.ts = (new Date).getTime()
      var n = d.createElement(t)
      n.src = "https://bat.bing.net/bat.js?ti=" + o.ti + ("uetq" != u ? "&q=" + u : "")
      n.async = 1
      n.onload = n.onreadystatechange = function() {
        var s = this.readyState
        s && "loaded" !== s && "complete" !== s || (
          o.q = w[u],
          w[u] = new UET(o),
          w[u].push("pageLoad"),
          n.onload = n.onreadystatechange = null
        )
      }
      var i = d.getElementsByTagName(t)[0]
      i.parentNode.insertBefore(n, i)
    })(window, document, "script", "uetq", { ti: tagId, enableAutoSpaTracking: true })

    logger.debug('✅ Microsoft Ads UET initialized successfully')
  } catch (error) {
    logger.error('❌ Error initializing Microsoft Ads UET:', error)
  }
}

/**
 * Track any Microsoft Ads custom event by name.
 * Skipped in test/staging mode.
 *
 * @param {string} eventName  e.g. 'begin_checkout', 'add_payment_info', 'purchase'
 * @param {object} [payload]  Additional event properties (revenue_value, currency, etc.)
 */
export const trackMicrosoftAdsEvent = (eventName, payload = {}) => {
  if (config.testMode) {
    logger.debug(`ℹ️ Microsoft Ads "${eventName}" event skipped: Test/Staging mode`)
    return
  }

  try {
    window.uetq = window.uetq || []
    window.uetq.push('event', eventName, payload)
    logger.debug(`📊 Microsoft Ads "${eventName}" event fired:`, payload)
  } catch (error) {
    logger.error(`❌ Error firing Microsoft Ads "${eventName}" event:`, error)
  }
}

/**
 * Track a Microsoft Ads custom "purchase" event on the conversion page.
 * Must be called AFTER initializeMicrosoftAds() has run (i.e. UET tag loaded).
 * Only fires in production (test mode is blocked by initializeMicrosoftAds, but
 * we guard here too so the function is safe to call unconditionally).
 *
 * @param {object} [options]
 * @param {number} [options.revenue]  Order value (e.g. product price)
 * @param {string} [options.currency] ISO 4217 currency code, defaults to 'USD'
 */
export const trackMicrosoftAdsPurchaseEvent = ({ revenue, currency = 'USD' } = {}) => {
  if (config.testMode) {
    logger.debug('ℹ️ Microsoft Ads purchase event skipped: Test/Staging mode')
    return
  }

  try {
    window.uetq = window.uetq || []

    const eventPayload = { event_category: 'purchase', currency }
    if (revenue != null && !isNaN(revenue)) {
      eventPayload.revenue_value = revenue
    }

    window.uetq.push('event', 'purchase', eventPayload)
    logger.debug('📊 Microsoft Ads purchase event fired:', eventPayload)
  } catch (error) {
    logger.error('❌ Error firing Microsoft Ads purchase event:', error)
  }
}

export default {
  initializeMicrosoftAds,
  trackMicrosoftAdsEvent,
  trackMicrosoftAdsPurchaseEvent,
}
